import type {
  BillingComparisonDto,
  BillingPeriod,
  BillingSummaryDto,
  OrderDto,
  RenewalDto,
  UsageDto,
} from "@/shared/api/types";
import { daysBetween, getOrgTodayString, toOrgDateString } from "@/shared/utils/dates";
import { payableOrder } from "@/shared/utils/orders";

/**
 * Where a subscription stands, as one object (plans/v1/M11-subscription-redesign.md §3).
 *
 * The Subscription page and the home tile answer the same question — *how long have I got and what is the
 * one thing to do about it* — and before this they answered it from different fields in different words.
 * Everything either of them needs is worked out here once, in the organisation's timezone, from the api's
 * own dates; the two surfaces only choose their wording.
 *
 * Days are COUNTED IN CALENDAR DAYS in `tz`, not in 24-hour blocks: a customer whose period ends tomorrow
 * morning has one day left, whatever the hour of the instant says.
 */

/**
 * `none` nothing ever bought · `active` running, nothing owed · `prepaid` the next window is already
 * paid for · `due` an invoice is waiting · `grace` the renewal was missed and the clock is running ·
 * `suspended` reads refused until it is paid · `cancelling` stops at the period end · `ended` stopped.
 */
export type SubscriptionState =
  "none" | "trial" | "active" | "prepaid" | "due" | "grace" | "suspended" | "cancelling" | "ended";

/** The one thing to do about it. `null` = nothing is owed and nothing is waiting. */
export type SubscriptionAction = "renew" | "pay" | "subscribe" | "resume" | "subscribe_again";

export interface SubscriptionStanding {
  state: SubscriptionState;
  action: SubscriptionAction | null;
  periodStart: string | null;
  periodEnd: string | null;
  /** The end of the last paid window when a renewal is already paid for; null otherwise. */
  paidThrough: string | null;
  /** The end of everything paid for: `paidThrough` when prepaid, else the period end. */
  coveredUntil: string | null;
  renewalAt: string | null;
  renewalPeriod: BillingPeriod;
  renewalTotalPaise: number | null;
  graceUntil: string | null;
  trialEndsAt: string | null;
  /** Whole days in `tz` until whatever runs out next; 0 means today or already past. */
  daysLeft: number;
  /** How much of the current period has run, 0–1; null while the dates are not both known. */
  progress: number | null;
  /** The invoice a "Pay" action opens. */
  payable: OrderDto | null;
  /** Something is already scheduled for the renewal, so the renewal is priced with it. */
  hasScheduledChanges: boolean;
}

/** `GET /billing`'s renewal block with the fields S6 added; null while the api sends none. */
export function renewalOf(billing: BillingSummaryDto | undefined): RenewalDto | null {
  return billing?.renewal ?? null;
}

/**
 * `GET /billing`'s comparison block. The schema carries it now, but an older api (or a test double) may
 * send none: that has no table to draw, which is a `null` here rather than a crash.
 */
export function comparisonOf(billing: BillingSummaryDto | undefined): BillingComparisonDto | null {
  return (billing as { comparison?: BillingComparisonDto | null } | undefined)?.comparison ?? null;
}

/** True while the customer has asked for the subscription to end at the period end. */
export function isCancelling(billing: BillingSummaryDto | undefined): boolean {
  return billing?.renewal?.cancelled === true || billing?.subscription?.cancelAtPeriodEnd === true;
}

/** The date a cancelling subscription stops, which is simply its current period end. */
export function endsAt(billing: BillingSummaryDto | undefined): string | null {
  return billing?.renewal?.at ?? billing?.subscription?.periodEnd ?? null;
}

/** The period the NEXT invoice will be billed at — the scheduled one, not the running one. */
export function renewalPeriod(billing: BillingSummaryDto | undefined): BillingPeriod {
  const sub = billing?.subscription;
  return billing?.renewal?.period ?? sub?.scheduledPeriod ?? sub?.period ?? "monthly";
}

/** Whole calendar days in `tz` from today until `at`; never negative. */
export function orgDaysUntil(at: string | null, tz: string, now: Date): number {
  const target = at ? toOrgDateString(at, tz) : null;
  return target ? Math.max(0, daysBetween(getOrgTodayString(tz, now), target)) : 0;
}

/**
 * The window a renewal paid today would buy: the renewal date to the same day of the next month (or
 * year), clamped to the last day of a short month. A LABEL — the order the api answers with carries the
 * authoritative dates — but "pay for 13 Oct – 13 Nov" is the difference between knowing and guessing.
 */
export function nextWindow(
  fromIso: string | null,
  period: BillingPeriod,
  tz: string,
): [string, string] | null {
  if (!fromIso) return null;
  const day = toOrgDateString(fromIso, tz);
  const parts = day.split("-").map(Number);
  const [year, month, date] = parts;
  if (year === undefined || month === undefined || date === undefined) return null;
  const months = period === "yearly" ? 12 : 1;
  const targetMonth = month - 1 + months;
  const endYear = year + Math.floor(targetMonth / 12);
  const endMonth = (targetMonth % 12) + 1;
  // The 31st of a month the next one does not have lands on its last day, the way every anniversary does.
  const lastOfMonth = new Date(Date.UTC(endYear, endMonth, 0)).getUTCDate();
  const endDay = Math.min(date, lastOfMonth);
  const pad = (n: number) => String(n).padStart(2, "0");
  return [day, `${endYear}-${pad(endMonth)}-${pad(endDay)}`];
}

function stateOf(billing: BillingSummaryDto, prepaid: boolean, payable: OrderDto | null) {
  const sub = billing.subscription;
  if (!sub) return "none";
  if (sub.status === "ended" || billing.suspendReason === "subscription_ended") return "ended";
  if (sub.status === "trial") return "trial";
  if (isCancelling(billing)) return "cancelling";
  if (sub.status === "overdue") return "grace";
  if (billing.tenantStatus === "suspended" || sub.status === "suspended") return "suspended";
  if (payable) return "due";
  return prepaid ? "prepaid" : "active";
}

const ACTION_OF: Record<SubscriptionState, SubscriptionAction | null> = {
  none: "subscribe",
  trial: "subscribe",
  active: "renew",
  prepaid: null,
  due: "pay",
  grace: "pay",
  suspended: "pay",
  cancelling: "resume",
  ended: "subscribe_again",
};

/** What runs out next, by state: the trial clock, the grace clock, or the end of what is paid for. */
function clockOf(state: SubscriptionState, standing: Omit<SubscriptionStanding, "daysLeft" | "progress">) {
  if (state === "trial") return standing.trialEndsAt ?? standing.periodEnd;
  if (state === "grace") return standing.graceUntil ?? standing.periodEnd;
  return standing.coveredUntil;
}

export function subscriptionStanding(
  billing: BillingSummaryDto | undefined,
  trial: UsageDto["trial"] | null | undefined,
  tz: string,
  now: Date = new Date(),
): SubscriptionStanding | null {
  if (!billing) return null;
  const sub = billing.subscription ?? null;
  const renewal = renewalOf(billing);
  const paidThrough = renewal?.paidThrough ?? null;
  const periodEnd = sub?.periodEnd ?? null;
  // "Prepaid" is a paid window that reaches PAST the running period; an api that always sends the current
  // end in the field is therefore read as the ordinary case rather than as a renewal nobody has paid.
  const prepaid =
    paidThrough !== null && (periodEnd === null || Date.parse(paidThrough) > Date.parse(periodEnd));
  const payable = payableOrder(billing.openOrders);
  const state = stateOf(billing, prepaid, payable);
  const facts = {
    state,
    action: ACTION_OF[state],
    periodStart: sub?.periodStart ?? null,
    periodEnd,
    paidThrough: prepaid ? paidThrough : null,
    coveredUntil: prepaid ? paidThrough : periodEnd,
    renewalAt: renewal?.at ?? periodEnd,
    renewalPeriod: renewalPeriod(billing),
    renewalTotalPaise: renewal?.totalPaise ?? sub?.renewalTotalPaise ?? null,
    graceUntil: sub?.graceUntil ?? null,
    // A trialling subscription's period end IS its trial end; `usage.trial` is the same date with a
    // countdown attached, and the card must still name the day when that read has not landed yet.
    trialEndsAt: trial?.endsAt ?? (sub?.status === "trial" ? sub.periodEnd : null),
    payable,
    hasScheduledChanges: (billing.scheduledChanges ?? []).length > 0,
  } satisfies Omit<SubscriptionStanding, "daysLeft" | "progress">;

  const start = facts.periodStart ? Date.parse(facts.periodStart) : null;
  const end = periodEnd ? Date.parse(periodEnd) : null;
  const progress =
    start !== null && end !== null && end > start
      ? Math.min(1, Math.max(0, (now.getTime() - start) / (end - start)))
      : null;
  return { ...facts, daysLeft: orgDaysUntil(clockOf(state, facts), tz, now), progress };
}
